import { PrimaryStar } from '@/components/ui/PrimaryStar';
import { useBackClosesDrawer, useReopenDrawerOnBack } from '@/lib/origin';
/**
 * Person drawer (UI review High #4): opened from any avatar or name with useUi().openPerson(userId).
 * Shows what the API returns for the caller; sections they can't see come back null and are left out.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { qk, usePersonProfile } from '@tradeoutlet/data';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Award, BadgeCheck, Briefcase, CalendarClock, CalendarRange, ChevronDown, ChevronRight, Copy, Building2, Ban, Flag, KeyRound, ListChecks, Lock, Mail, MessageSquare, MoreHorizontal, Pencil, Phone, Truck, Unlock, Users } from 'lucide-react';
import { ASSIGN_TASK_LABEL, canOpenUserDetails, contractTypeLabel, userDetailsPath, viewUserDetailsLabel, personRotaHref, showAssignTask, showViewRota, viewRotaLabel, type PersonProfile } from '@tradeoutlet/shared';
import { PersonInvitation } from './users/InviteLink';
import { SetPinDialog } from './SetPinDialog';
import { EditUserDialog } from './users/EditUser';
import { holdsAnywhere } from '@/lib/navAccess';
import { usePermissions } from '@/lib/permissions';
import { KudosSheet } from '@/mobile/KudosSheet';
import { NewTaskModal } from '@/features/tasks/TasksArea';
import { PersonTwoFactor } from '@/mobile/people/PersonTwoFactor';
import { PersonDeleteMember } from '@/mobile/people/PersonDeleteMember';
import { PersonManage, UpcomingShifts } from '@/mobile/people/PersonManage';
import { DateTime } from 'luxon';
import { RelativeTime } from '@/components/time/RelativeTime';
import { api, errorMessage } from '@/lib/api';
import { useFollow } from '@/lib/follows';
import { FollowToggle } from '@/features/buildings/common';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { formatDate, sentenceCase } from '@/lib/format';
import { Avatar, Badge, Button, SheetClose, Spinner, ask } from '@/components/ui';
import { useMobileLayout } from '@/mobile/layout';
import { useStartCall } from '@/features/calls/useStartCall';
import { usePrepareCall } from '@/features/calls/prepare';
import { useModuleOn } from '@/lib/modules';
import { openReport, useToggleBlock } from '@/features/moderation/report';
import { BLOCK_TEXT, REPORT_TEXT } from '@tradeoutlet/shared';

import { JobTitleLine } from './JobTitleEdit';
import { PresenceLine, usePersonPresence } from './PresenceLine';
const KudosListModal = lazy(() => import('./KudosListModal'));

export function SkillPill({ s }: { s: PersonProfile['skills'][number] }) {
  const tone = s.state === 'expired' ? 'text-rose-800 dark:text-rose-200' : s.state === 'expiring' ? 'text-amber-900 dark:text-amber-200' : 'text-emerald-800 dark:text-emerald-200';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${tone}`} title={s.expires_on ? `Expires ${formatDate(s.expires_on, 'd LLL yyyy')}` : 'No expiry'}>
      <BadgeCheck className="h-3 w-3" />{s.name}{s.expires_on && <span className="opacity-80">· {s.state === 'expired' ? 'expired' : formatDate(s.expires_on, 'd LLL yyyy')}</span>}
    </span>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-t border-slate-100 px-5 py-4 dark:border-slate-800">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{icon}{title}</h3>
      {children}
    </section>
  );
}

/**
 * A sensitive section that starts collapsed (t138u). Permissions still decide whether it renders at all; this only
 * folds it. State lives in the component, and the drawer body is keyed by person, so it resets for each profile.
 */
function Fold({ title, icon, hint, children }: { title: string; icon?: React.ReactNode; hint?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = `fold-${title.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <section className="border-t border-slate-100 dark:border-slate-800">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)} data-testid={id}
        className="flex w-full items-center gap-1.5 px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-slate-900 dark:hover:bg-slate-800/60 dark:focus-visible:outline-white">
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />{icon}{title}
        {!open && hint && <span className="ml-auto truncate pl-2 text-[11px] font-normal normal-case tracking-normal text-slate-400">{hint}</span>}
      </button>
      {open && <div id={id} className="px-5 pb-4">{children}</div>}
    </section>
  );
}

function ContactRow({ icon, href, value, label }: { icon: React.ReactNode; href: string; value: string; label: string }) {
  const toast = useUi(s => s.toast);
  const copy = async () => { try { await navigator.clipboard.writeText(value); toast(`Copied ${label}`, 'success'); } catch { toast('Couldn’t copy. Select the text instead.', 'error'); } };
  return (
    <li className="flex items-center gap-2">
      {icon}<a className="min-w-0 truncate hover:underline" href={href}>{value}</a>
      <button type="button" onClick={() => void copy()} aria-label={`Copy ${label}`} title={`Copy ${label}`}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 dark:hover:bg-slate-800 dark:hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
    </li>
  );
}

const hm = (iso: string) => DateTime.fromISO(iso).setZone('Europe/London').toFormat('HH:mm');

/** Today (t138u): their shift, clock-in badge and your recent messages with them. Each part is absent when the API says you can't see it. */
function AtAGlance({ p, onOpenChat, compact }: { p: PersonProfile; onOpenChat: (conversationId: string) => void; /** t231u: phone, no hint text. */ compact?: boolean }) {
  const first = p.display_name.split(' ')[0];
  const sh = p.today?.shift;
  const shiftText = !p.today ? null : !sh ? 'No upcoming shifts'
    : `${p.today.working_today ? 'Working today' : `Next shift: ${DateTime.fromISO(sh.local_date).toFormat('ccc d LLL')},`} ${hm(sh.starts_at)}–${hm(sh.ends_at)}${sh.building ? ` at ${sh.building}` : ''}`;
  const c = p.clock;
  const badge = !c ? null : c.state === 'in' ? { t: c.building ? `In at ${c.building} since ${hm(c.at!)}` : `Clocked in ${hm(c.at!)}`, cls: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900' }
    : c.state === 'break' ? { t: c.building ? `On break at ${c.building}` : 'On break', cls: 'bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900' }
    : c.state === 'out' ? { t: `Clocked out ${hm(c.at!)}`, cls: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700' }
    : { t: 'Not clocked in yet', cls: c.late ? 'bg-amber-50 text-amber-900 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800' : 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700' };
  if (!shiftText && !badge && !p.dm) return null;
  return (
    <section className="border-t border-slate-100 px-5 py-3 dark:border-slate-800" data-testid="person-today">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><CalendarClock className="h-3.5 w-3.5" />Today</h3>
      {(shiftText || badge) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {shiftText && <span className="text-slate-800 dark:text-slate-100" data-testid="person-shift"
            title={sh ? [sh.position, sh.building, DateTime.fromISO(sh.local_date).toFormat('cccc d LLLL yyyy')].filter(Boolean).join(' · ') : undefined}>{shiftText}</span>}
          {badge && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${badge.cls}`} data-testid="person-clock"
            title={c?.state === 'not_in' && c.late ? 'Their shift has started' : undefined}>{badge.t}</span>}
        </div>
      )}
      {p.dm && (p.dm.messages.length > 0 || !compact) && (
        <div className="mt-3" data-testid="person-recent-dm">
          {p.dm.messages.length ? (
            <>
              <ul className="space-y-1">
                {p.dm.messages.map(m => (
                  <li key={m.id} className="flex items-baseline gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200" title={m.preview}><span className="font-medium text-slate-900 dark:text-white">{m.mine ? 'You' : first}:</span> {m.preview}</span>
                    <RelativeTime iso={m.created_at} className="shrink-0 text-xs text-slate-500" />
                  </li>
                ))}
              </ul>
              <button type="button" className="mt-1.5 text-xs font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900 dark:text-slate-200" onClick={() => onOpenChat(p.dm!.conversation_id!)}>Open chat</button>
            </>
          ) : compact ? null : <p className="text-sm text-slate-500">No messages with {first} yet. Use <strong className="font-medium text-slate-700 dark:text-slate-200">Message</strong> above to start a chat.</p>}
        </div>
      )}
    </section>
  );
}

/**
 * Where someone works (t137u), as labelled rows instead of one mixed list: Buildings (a star on the primary one when there are several), Department,
 * Positions, Groups. Empty rows are hidden. Building and team chips link to their pages; the drawer passes a
 * "Back to <name>" origin and its history entry reopens the drawer on Back. Also used by My account.
 */
export function WorkDetails({ p, linkState, onNavigate, bare }: {
  p: Pick<PersonProfile, 'buildings' | 'teams' | 'positions'>; linkState?: unknown; onNavigate?: () => void;
  /** No section chrome (My account renders its own). */ bare?: boolean;
}) {
  const depts = p.teams.filter(t => t.kind !== 'group');
  const groups = p.teams.filter(t => t.kind === 'group');
  const chip = 'inline-flex max-w-full items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-800 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800';
  const rows: { label: string; testId: string; items: React.ReactNode[] }[] = [
    { label: p.buildings.length > 1 ? 'Buildings' : 'Building', testId: 'work-buildings', items: [...p.buildings].sort((a, b) => Number(b.primary) - Number(a.primary)).map(b => (
      <Link key={b.id} to={`/buildings/${b.id}`} state={linkState} onClick={onNavigate} className={chip}>
        <Building2 className="h-3 w-3 shrink-0 text-slate-500" aria-hidden /><span className="truncate">{b.name}</span>{b.primary && p.buildings.length > 1 && <PrimaryStar />}
      </Link>)) },
    { label: depts.length > 1 ? 'Departments' : 'Department', testId: 'work-departments', items: depts.map(t => (
      <Link key={t.id} to={`/groups?team=${t.id}`} state={linkState} onClick={onNavigate} className={chip}>
        <Users className="h-3 w-3 shrink-0 text-slate-500" aria-hidden /><span className="truncate">{t.name}</span>
      </Link>)) },
    { label: 'Positions', testId: 'work-positions', items: p.positions.map(x => <Badge key={x.id} color={x.color}>{x.name}</Badge>) },
    { label: 'Groups', testId: 'work-groups', items: groups.map(t => (
      <Link key={t.id} to={`/groups?team=${t.id}`} state={linkState} onClick={onNavigate} className={chip}>
        <span className="truncate">{t.name}</span>
      </Link>)) },
  ].filter(r => r.items.length);
  if (!rows.length) return null;
  const body = (
    <dl className="grid grid-cols-[7.5rem_1fr] gap-x-3 gap-y-2 text-sm" data-testid="work-details">
      {rows.map(r => (
        <div key={r.label} className="contents" data-testid={r.testId}>
          <dt className="pt-0.5 text-xs font-medium text-slate-500">{r.label}</dt>
          <dd className="flex min-w-0 flex-wrap gap-1">{r.items}</dd>
        </div>
      ))}
    </dl>
  );
  return bare ? body : <Section title="Work details" icon={<Building2 className="h-3.5 w-3.5" />}>{body}</Section>;
}

export function PersonDrawer() {
  const id = useUi(s => s.personId);
  const close = useUi(s => s.openPerson);
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const toast = useUi(s => s.toast);
  const ref = useRef<HTMLDivElement>(null);
  const q = usePersonProfile(ws, id);
  const presence = usePersonPresence(ws, id);
  const loc = useLocation();
  // Phase 0: on the phone layout the drawer is a full-screen sheet with a 44px action row (docs/mobile-design.md §3.6).
  const mobile = useMobileLayout();
  const startCall = useStartCall();
  const callsOn = useModuleOn('calls'); // t369u
  usePrepareCall(callsOn && Boolean(id), id); // t437u: the sheet's Call connects sooner
  const [kudosOpen, setKudosOpen] = useState(false);
  const blocks = useToggleBlock(); // t536u
  const [more, setMore] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  // t231u: on the phone, Kudos opens the kudos sheet over the profile with them picked.
  const [giving, setGiving] = useState(false);
  const [assigning, setAssigning] = useState(false); // t473u
  const [settingPin, setSettingPin] = useState(false); // t712u
  const mine = usePermissions().data;
  const canKudos = holdsAnywhere(mine, 'kudos.post');
  const canUserDetails = canOpenUserDetails(mine); // t567u
  useEffect(() => { setKudosOpen(false); setMore(false); setGiving(false); setAssigning(false); }, [id]);
  useBackClosesDrawer(Boolean(id), () => close(null), id ? `person:${id}` : true);
  useReopenDrawerOnBack('person:', pid => close(pid));
  useEffect(() => {
    if (!id) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[role="dialog"][aria-modal="true"]')) close(null); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [id, close]);
  if (!id) return null;
  const p = q.data;
  const go = (to: string) => { close(null); nav(to); };
  const message = async () => {
    try { const c = await api.openDm(ws!, [id]); go(`/c/${c.id}`); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const call = async () => {
    try { const c = await api.openDm(ws!, [id]); close(null); await startCall({ conversation_id: c.id, media: 'audio' }); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  // t712u: managers set or reset someone's kiosk PIN; it's never shown after saving.
  const pinChanged = () => void queryClient.invalidateQueries({ queryKey: qk.person(ws, id) });
  const resetPin = async () => {
    if (!q.data || !await ask({ title: 'Reset kiosk PIN?', body: `${q.data.display_name}'s PIN stops working. The kiosk will ask them to set a new one in the app before they clock in with a PIN.`, confirmLabel: 'Reset PIN', danger: true })) return;
    try { await api.wf.resetKioskPinFor(ws!, id!); toast('PIN reset', 'success'); pinChanged(); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const unlock = async () => {
    try { await api.wf.unlockKioskUser(ws!, id); toast('Kiosk unlocked', 'success'); void queryClient.invalidateQueries({ queryKey: qk.person(ws, id) }); } catch (e) { toast(errorMessage(e), 'error'); }
  };

  return (
    <>
      {!mobile && <div className="fixed inset-0 z-40 bg-black/30" onClick={() => close(null)} aria-hidden />}
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal={mobile ? 'true' : 'false'} aria-label={p ? `Profile: ${p.display_name}` : 'Profile'} data-testid={mobile ? 'person-sheet' : 'person-drawer'}
        className={mobile ? 'fixed inset-0 z-50 flex flex-col bg-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] outline-none dark:bg-slate-900'
          : 'fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl outline-none dark:bg-slate-900'}>
        {mobile
          ? <div className="flex items-center px-1"><button type="button" aria-label="Close profile" onClick={() => close(null)} className="flex h-11 w-11 items-center justify-center rounded-full active:bg-slate-100 dark:active:bg-slate-800"><ChevronDown className="h-6 w-6" /></button></div>
          : <div className="flex h-11 shrink-0 justify-end px-2 pt-2"><SheetClose inline onClose={() => close(null)} /></div>}
        {q.isLoading ? <div className="p-6"><Spinner /></div> : q.isError || !p ? <div className="space-y-3 p-6"><p className="text-sm text-slate-500">This person isn’t in the workspace yet or any more, or you can’t see their profile.</p><PersonInvitation userId={id} /></div> : (
          <div key={p.user_id} className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-6">
            <div className="flex items-start gap-4 px-5 pb-4">
              <Avatar size={64} name={p.display_name} id={p.user_id} url={p.avatar_url} presence={p.active ? presence.status : undefined} />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">{p.display_name}</h2>
                {p.active && presence.loaded && <PresenceLine status={presence.status} lastSeenAt={presence.lastSeenAt} />}
                <JobTitleLine ws={ws!} person={p} />
                {/* t514u: Not set up yet · Invited 3 days ago, with Resend invite (desktop and phone). */}
                <div className="mt-2"><PersonInvitation userId={p.user_id} email={p.email} /></div>
                {(p.status_emoji || p.status_text) && <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{p.status_emoji} {p.status_text}</div>}
                <div className="mt-1 flex flex-wrap gap-1">
                  {!p.active && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">Deactivated</Badge>}
                  {p.is_external && <Badge>Self-employed driver</Badge>}
                  {p.kudos_90d > 0 && (
                    <button type="button" onClick={() => setKudosOpen(true)} data-testid="person-kudos-90d" title="See them"
                      className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2">
                      <Badge className="cursor-pointer hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:text-white"><Award className="h-3 w-3" />{p.kudos_90d} kudos in 90 days</Badge>
                    </button>
                  )}
                </div>
              </div>
            </div>
            {!p.is_me && mobile && (
              <div className={`grid ${['', '', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][2 + Number(callsOn) + Number(canKudos && !p.is_external)]} gap-2 px-5 pb-4`} data-testid="person-actions">
                <SheetAction icon={<MessageSquare className="h-5 w-5" />} label="Message" primary onClick={() => void message()} />
                {callsOn && <SheetAction icon={<Phone className="h-5 w-5" />} label="Call" onClick={() => void call()} />}
                {canKudos && !p.is_external && <SheetAction icon={<Award className="h-5 w-5" />} label="Kudos" onClick={() => setGiving(true)} />}
                <SheetAction icon={<MoreHorizontal className="h-5 w-5" />} label="More" expanded={more} onClick={() => setMore(v => !v)} />
              </div>
            )}
            {!p.is_me && mobile && more && (
              <ul className="mx-5 mb-4 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700" data-testid="person-more">
                {showAssignTask(p) && <li><button type="button" className={moreRow} onClick={() => setAssigning(true)} data-testid="person-assign-task"><ListChecks className="h-5 w-5 text-slate-500" />{ASSIGN_TASK_LABEL}</button></li>}
                {showViewRota(p) && <li><button type="button" className={moreRow} onClick={() => go(personRotaHref(p, 'phone'))} data-testid="person-view-rota"><CalendarRange className="h-5 w-5 text-slate-500" />{viewRotaLabel(p)}</button></li>}
                <FollowPersonItem id={p.user_id} name={p.display_name} />
                {p.email && <li><a href={`mailto:${p.email}`} className={moreRow}><Mail className="h-5 w-5 text-slate-500" />Email</a></li>}
                {p.phone && <li><a href={`tel:${p.phone.replace(/\s+/g, '')}`} className={moreRow}><Phone className="h-5 w-5 text-slate-500" />{p.phone}</a></li>}
                <li><button type="button" className={moreRow} onClick={() => void blocks.toggle(p.user_id, p.display_name)} data-testid="person-block"><Ban className="h-5 w-5 text-slate-500" />{blocks.isBlocked(p.user_id) ? BLOCK_TEXT.unblock : BLOCK_TEXT.block}</button></li>
                <li><button type="button" className={moreRow} onClick={() => openReport({ type: 'person', id: p.user_id, name: p.display_name })} data-testid="person-report"><Flag className="h-5 w-5 text-slate-500" />{REPORT_TEXT.menu}</button></li>
              </ul>
            )}
            {!p.is_me && !mobile && (
              <div className="flex flex-wrap gap-2 px-5 pb-4">
                <Button size="sm" onClick={() => void message()}><MessageSquare className="h-4 w-4" />Message</Button>
                {!p.is_external && <Button size="sm" variant="secondary" onClick={() => go(`/kudos?give=${p.user_id}&from=person`)}><Award className="h-4 w-4" />Give kudos</Button>}
                {showAssignTask(p) && <Button size="sm" variant="secondary" onClick={() => setAssigning(true)} data-testid="person-assign-task"><ListChecks className="h-4 w-4" />{ASSIGN_TASK_LABEL}</Button>}
                {showViewRota(p) && <Button size="sm" variant="secondary" onClick={() => go(personRotaHref(p, 'desktop'))} data-testid="person-view-rota"><CalendarRange className="h-4 w-4" />{viewRotaLabel(p)}</Button>}
                <FollowPerson id={p.user_id} name={p.display_name} />
                {p.can.edit_details && <Button size="sm" variant="secondary" onClick={() => setEditingDetails(true)} data-testid="person-edit-details"><Pencil className="h-4 w-4" />Edit details</Button>}
                <Button size="sm" variant="ghost" onClick={() => void blocks.toggle(p.user_id, p.display_name)} data-testid="person-block"><Ban className="h-4 w-4" />{blocks.isBlocked(p.user_id) ? BLOCK_TEXT.unblock : BLOCK_TEXT.block}</Button>
                <Button size="sm" variant="ghost" onClick={() => openReport({ type: 'person', id: p.user_id, name: p.display_name })} data-testid="person-report"><Flag className="h-4 w-4" />{REPORT_TEXT.menu}</Button>
              </div>
            )}
            {!p.is_me && blocks.isBlocked(p.user_id) && (
              <p className="mx-5 mb-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300" data-testid="person-blocked-note">{BLOCK_TEXT.note}</p>
            )}
            {editingDetails && <EditUserDialog open userId={p.user_id} onClose={() => setEditingDetails(false)} />}
            {p.is_me && (
              <div className="flex flex-wrap gap-2 px-5 pb-4">
                <Button size="sm" variant="secondary" onClick={() => go('/settings/account/profile')}><Pencil className="h-4 w-4" />Edit my profile</Button>
                {showViewRota(p) && <Button size="sm" variant="secondary" onClick={() => go(personRotaHref(p, mobile ? 'phone' : 'desktop'))} data-testid="person-view-rota"><CalendarRange className="h-4 w-4" />{viewRotaLabel(p)}</Button>}
              </div>
            )}

            <AtAGlance p={p} compact={mobile} onOpenChat={cid => go(`/c/${cid}`)} />
            {mobile && <UpcomingShifts p={p} onNavigate={() => close(null)} />}
            {mobile && <PersonManage p={p} onNavigate={to => (to ? go(to) : close(null))} />}
            {mobile && <PersonTwoFactor p={p} />}
            {mobile && <PersonDeleteMember p={p} onDeleted={() => close(null)} />}

            <WorkDetails p={p} linkState={{ from: { key: 'person', path: `${loc.pathname}${loc.search}`, label: p.display_name } }} onNavigate={() => close(null)} />
            {p.skills.length > 0 && (
              <Section title="Skills" icon={<BadgeCheck className="h-3.5 w-3.5" />}>
                <div className="flex flex-wrap gap-1">{p.skills.map(s => <SkillPill key={s.id} s={s} />)}</div>
                {p.can.manage_skills && <Link to="/workforce/skills" onClick={() => close(null)} className="mt-2 inline-block text-xs text-slate-600 underline dark:text-slate-300">Manage skills</Link>}
              </Section>
            )}

            {(p.email || p.phone) && (
              <Fold title="Contact" icon={<Mail className="h-3.5 w-3.5" />} hint={p.phone ? 'Email and phone' : 'Email'}>
                <ul className="space-y-1.5 text-sm" data-testid="person-contact">
                  {p.email && <ContactRow icon={<Mail className="h-4 w-4 text-slate-500" />} href={`mailto:${p.email}`} value={p.email} label="email address" />}
                  {p.phone && <ContactRow icon={<Phone className="h-4 w-4 text-slate-500" />} href={`tel:${p.phone.replace(/\s+/g, '')}`} value={p.phone} label="phone number" />}
                </ul>
              </Fold>
            )}

            {p.employment && (
              <Fold title="Employment" icon={<Briefcase className="h-3.5 w-3.5" />} hint={contractTypeLabel(p.employment.type)}>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-slate-500">Type</dt><dd>{contractTypeLabel(p.employment.type)}</dd>
                  <dt className="text-slate-500">Hours / week</dt><dd>{p.employment.contracted_hours_per_week ?? '—'}</dd>
                  {p.employment.scheduled_minutes_week != null && <><dt className="text-slate-500">Scheduled this week</dt>
                    <dd data-testid="person-scheduled-week">{Math.round(p.employment.scheduled_minutes_week / 6) / 10}h{p.employment.contracted_hours_per_week ? ` of ${p.employment.contracted_hours_per_week}h` : ''}</dd></>}
                  <dt className="text-slate-500">Started</dt><dd>{p.employment.start_date ? formatDate(p.employment.start_date, 'd LLL yyyy') : '—'}</dd>
                  <dt className="text-slate-500">Rota access</dt><dd>{sentenceCase(p.employment.workforce_role)}</dd>
                </dl>
                {(p.can.edit_employment || p.can.view_hr) && <div className="mt-3 flex flex-wrap gap-2">
                  {p.can.edit_employment && <Button size="sm" variant="secondary" onClick={() => go(`/settings/people/users/${p.user_id}?tab=employment`)}><Pencil className="h-4 w-4" />Edit employment</Button>}
                  {(p.can.view_hr || p.can.edit_employment) && <Button size="sm" variant="ghost" onClick={() => go(`/settings/people/users/${p.user_id}?tab=hr`)} data-testid="person-hr-record">Personal details</Button>}
                </div>}
              </Fold>
            )}

            {p.kiosk && (
              <Fold title="Clock-in" icon={<Lock className="h-3.5 w-3.5" />} hint={p.kiosk.locked ? 'Locked out' : p.kiosk.has_pin ? 'PIN set' : 'No PIN'}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge>{p.kiosk.has_pin ? 'PIN set' : 'No PIN'}</Badge>
                  <Badge>{p.kiosk.has_badge ? 'Badge issued' : 'No badge'}</Badge>
                  {p.kiosk.locked && <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">Locked out</Badge>}
                  {p.can.unlock_kiosk && <Button size="sm" variant="secondary" onClick={() => void unlock()}><Unlock className="h-4 w-4" />Unlock</Button>}
                  {p.can.manage_kiosk_pin && <Button size="sm" variant="secondary" onClick={() => setSettingPin(true)} data-testid="person-pin-set">{p.kiosk.has_pin ? 'Change PIN' : 'Set PIN'}</Button>}
                  {p.can.manage_kiosk_pin && p.kiosk.has_pin && <Button size="sm" variant="ghost" onClick={() => void resetPin()} data-testid="person-pin-reset">Reset PIN</Button>}
                </div>
              </Fold>
            )}

            {p.driver && (
              <Fold title="Driver" icon={<Truck className="h-3.5 w-3.5" />} hint={p.driver.licence_categories.join(', ') || undefined}>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  {p.driver.company_name && <><dt className="text-slate-500">Company</dt><dd>{p.driver.company_name}</dd></>}
                  <dt className="text-slate-500">Licence</dt><dd>{p.driver.licence_categories.join(', ') || '—'}{p.driver.licence_expires_on ? ` · to ${formatDate(p.driver.licence_expires_on, 'd LLL yyyy')}` : ''}</dd>
                  <dt className="text-slate-500">CPC</dt><dd>{p.driver.cpc_expires_on ? formatDate(p.driver.cpc_expires_on, 'd LLL yyyy') : '—'}</dd>
                  <dt className="text-slate-500">Points</dt><dd>{p.driver.points}</dd>
                </dl>
              </Fold>
            )}

            {p.roles && (
              <Fold title="Roles" icon={<KeyRound className="h-3.5 w-3.5" />} hint={p.roles.length ? `${p.roles.length} role${p.roles.length === 1 ? '' : 's'}` : 'None'}>
                {p.roles.length ? <ul className="space-y-0.5 text-sm">{p.roles.map((r, i) => <li key={i}>{r.name} <span className="text-slate-500">· {r.scope}</span></li>)}</ul> : <p className="text-sm text-slate-500">No roles granted directly.</p>}
                <Button size="sm" variant="secondary" className="mt-3" onClick={() => go(`/settings/people/access?user=${p.user_id}`)}>Check access</Button>
              </Fold>
            )}

            {canUserDetails && (
              <div className="border-t border-slate-100 px-5 pt-3 dark:border-slate-800">
                <button type="button" onClick={() => go(userDetailsPath(p.user_id))} data-testid="person-user-details"
                  className="flex min-h-11 w-full items-center justify-between gap-2 text-left text-sm font-medium text-slate-800 hover:underline dark:text-slate-100">
                  {viewUserDetailsLabel(p.display_name)}<ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {assigning && p && <NewTaskModal defaultAssignee={p.user_id} onClose={() => setAssigning(false)} onCreated={() => close(null)} />}
      {giving && p && <KudosSheet to={p.user_id} onClose={() => setGiving(false)} />}
      {settingPin && p && <SetPinDialog userId={p.user_id} name={p.display_name} onClose={() => setSettingPin(false)} onSaved={pinChanged} />}
      {kudosOpen && p && <Suspense fallback={null}><KudosListModal userId={p.user_id} name={p.display_name} onClose={() => setKudosOpen(false)} /></Suspense>}
    </>
  );
}

/** Phone person sheet: one of the four 44px actions under the header. */
function SheetActionFace({ icon, label, primary }: { icon: React.ReactNode; label: string; primary?: boolean }) {
  return (
    <span className={`flex min-h-[56px] w-full flex-col items-center justify-center gap-0.5 rounded-xl text-xs font-medium ${primary ? 'bg-accent text-on-accent' : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'}`}>
      {icon}{label}
    </span>
  );
}
function SheetAction({ onClick, disabled, expanded, ...face }: { icon: React.ReactNode; label: string; primary?: boolean; disabled?: boolean; expanded?: boolean; onClick: () => void }) {
  return <button type="button" onClick={onClick} disabled={disabled} aria-expanded={expanded} className="disabled:opacity-40" data-testid={`person-action-${face.label.toLowerCase()}`}><SheetActionFace {...face} /></button>;
}
const moreRow = 'flex min-h-[48px] w-full items-center gap-3 border-b border-slate-100 px-4 text-left text-base last:border-0 active:bg-slate-100 dark:border-slate-800 dark:active:bg-slate-800';
function FollowPersonItem({ id, name }: { id: string; name: string }) {
  const f = useFollow('person', id, name);
  return <li><button type="button" className={moreRow} onClick={() => void f.toggle()}><Users className="h-5 w-5 text-slate-500" />{f.following ? 'Unfollow' : 'Follow'}</button></li>;
}

/** t139: follow a person so they're listed under Home → People in the sidebar. */
function FollowPerson({ id, name }: { id: string; name: string }) {
  const f = useFollow('person', id, name);
  return <FollowToggle following={f.following} name={name} onToggle={() => void f.toggle()} />;
}

/**
 * Wrap an avatar or name so clicking it opens the person drawer. Pointer and key events stop here, so a name inside
 * something draggable or clickable (a shift block, a day cell, a table row) opens the profile without starting a
 * drag or opening the parent.
 */
export function PersonButton({ userId, name, className, children, tabIndex, testId }: {
  userId: string | null | undefined; name?: string | null; className?: string; children: React.ReactNode; tabIndex?: number; testId?: string;
}) {
  const open = useUi(s => s.openPerson);
  if (!userId) return <>{children}</>;
  return (
    // t163: pointer + focus ring always (buttons default to an arrow cursor); preventDefault so a name inside a card
    // link (e.g. an announcement) opens the person, not the card.
    <button type="button" className={`cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 ${className ?? 'inline-flex items-center gap-1 rounded hover:underline'}`}
      onClick={e => { e.preventDefault(); e.stopPropagation(); open(userId); }}
      onPointerDown={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} tabIndex={tabIndex} data-testid={testId ?? 'person-button'}
      aria-label={name ? `View ${name}’s profile` : 'View profile'}>
      {children}
    </button>
  );
}

